import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { ImageFile } from '@components/editor-page/editor-pane/tool-bar/image-button/edit-image-modal';
import { isDevMode } from '../utils/test-modes';
import authStore from '../utils/auth-store';

const BASE_API_URL = 'cms/media';

export const imageHelperApi = createApi({
  reducerPath: 'imageHelperApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${
      isDevMode ? process.env.NEXT_PUBLIC_STAGING_URL : ''
    }topics/api/v1/`,
    prepareHeaders: (headers) => {
      const jwt = authStore.get().jwt;
      if (jwt) {
        headers.set('X-User-Token', jwt);
      }
      return headers;
    }
  }),
  tagTypes: [
    'GetImageLibrary',
    'GetImageData',
    'UploadImage',
    'UpdateImageData',
    'ValidateImage'
  ],
  endpoints: (builder) => ({
    getImageLibrary: builder.query({
      query: (args: {
        currentPage: number;
        search: string;
        perPage: number;
      }) => {
        const { currentPage = 1, search = '', perPage = 20 } = args;
        return {
          method: 'GET',
          url: `${BASE_API_URL}/search/?per_page=${perPage}&current_page=${currentPage}${
            search !== '' ? `&name_q=${search}` : ''
          }`
        };
      }
    }),
    getImageDataById: builder.query({
      query: (id: string) => `${BASE_API_URL}/${id}`,
      transformResponse: (response) => {
        const attributes = response.data[0].attributes;
        const fileObj = {} as ImageFile;
        fileObj.id = response.data[0].id;;
        fileObj.name = attributes.filename;
        fileObj.url = attributes.url;
        fileObj.tags = attributes.tags ?? [];
        fileObj.logs = attributes.logs;
        fileObj.size = attributes.file_size;
        fileObj.dimension = attributes.file_dimension;
        return fileObj;
      }
    }),
    uploadImage: builder.mutation({
      invalidatesTags: ['UploadImage'],
      query: (file) => {
        const image = new FormData();
        image.append('image', file);
        image.append('filename', file.name);
        return { url: `${BASE_API_URL}/`, method: 'POST', body: image };
      }
    }),
    updateImageData: builder.mutation({
      invalidatesTags: ['UpdateImageData'],
      query: (args) => {
        const { id, originalObject } = args;
        return {
          url: `${BASE_API_URL}/${id}`,
          method: 'PUT',
          body: originalObject
        };
      }
    }),
    validateImage: builder.query({
      query: (filename: string) =>
        `${BASE_API_URL}/validate?filename=${filename}`,
      providesTags: ['ValidateImage']
    })
  })
});

export const {
  useGetImageLibraryQuery,
  useGetImageDataByIdQuery,
  useUploadImageMutation,
  useUpdateImageDataMutation,
  useValidateImageQuery,
} = imageHelperApi;

export type ImageHelperApiReducer = typeof imageHelperApi.reducer;
